import { useMemo, useState } from 'react';
import { api, formatTime, mutate } from '../api';
import { useLoad } from '../hooks';
import { can, useIdentity } from '../context';
import { ConfirmButton, Empty, ErrorPanel, Loading, Modal, PageHeader, StatCard, StatusBadge } from '../components/Common';

type CodeForm = {
  count: number;
  days: number;
  maxUses: number;
  note: string;
};

type EditForm = {
  id: string;
  isPermanent: boolean;
  days: number;
  maxUses: number;
  expiresAtDay: string;
  status: 'active' | 'disabled';
  note: string;
};

type ActivationCodeRow = {
  id: string;
  codePrefix: string;
  code: string | null;
  durationSeconds: number | null;
  isPermanent: number | boolean;
  maxUses: number;
  usedCount: number;
  status: 'active' | 'disabled' | 'exhausted';
  expiresAt: number | null;
  note: string;
  createdBy: string;
  createdAt: number;
};

const defaultForm: CodeForm = { count: 1, days: 30, maxUses: 1, note: '后台生成' };

function isPermanent(row: Pick<ActivationCodeRow, 'isPermanent'>): boolean {
  return row.isPermanent === true || Number(row.isPermanent) === 1;
}

function secondsToDays(value: unknown): number {
  const seconds = Number(value || 0);
  if (!seconds) return 30;
  return Math.max(1, Math.round(seconds / 86400));
}

function toDateInput(value: unknown): string {
  const seconds = Number(value || 0);
  if (!seconds) return '';
  const date = new Date(seconds * 1000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function dayEndUnix(value: string): number | null {
  if (!value) return null;
  return Math.floor(new Date(`${value}T23:59:59`).getTime() / 1000);
}

export function ActivationCodesPage() {
  const identity = useIdentity();
  const state = useLoad(() => api<{ items: ActivationCodeRow[] }>('/activation-codes?limit=100'), []);
  const [created, setCreated] = useState<string[]>([]);
  const [form, setForm] = useState<CodeForm | null>(null);
  const [editing, setEditing] = useState<EditForm | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const items = state.data?.items || [];
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((row) => row.status === 'active').length,
    exhausted: items.filter((row) => row.status === 'exhausted').length,
    disabled: items.filter((row) => row.status === 'disabled').length
  }), [items]);

  async function copyCode(code: string) {
    await navigator.clipboard.writeText(code);
    setMessage('激活码已复制');
  }

  async function create() {
    if (!form) return;
    setError('');
    setMessage('');
    try {
      const count = Math.max(1, Math.min(50, Math.floor(Number(form.count || 1))));
      const days = Math.max(0, Math.floor(Number(form.days || 0)));
      const maxUses = Math.max(1, Math.min(1000, Math.floor(Number(form.maxUses || 1))));
      const result = await mutate<{ codes: string[] }>('/activation-codes', 'POST', {
        count,
        planCode: days === 0 ? 'permanent' : 'custom',
        isPermanent: days === 0,
        durationSeconds: days === 0 ? null : days * 86400,
        maxUses,
        note: form.note.trim()
      }, crypto.randomUUID());
      setCreated(result.codes || []);
      setMessage('激活码已生成，并已保存到记录');
      setForm(null);
      await state.reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  function openEdit(row: ActivationCodeRow) {
    setError('');
    setMessage('');
    setEditing({
      id: row.id,
      isPermanent: isPermanent(row),
      days: secondsToDays(row.durationSeconds),
      maxUses: Number(row.maxUses || 1),
      expiresAtDay: toDateInput(row.expiresAt),
      status: row.status === 'disabled' ? 'disabled' : 'active',
      note: row.note || ''
    });
  }

  async function saveEdit() {
    if (!editing) return;
    setError('');
    setMessage('');
    try {
      const days = Math.max(1, Math.floor(Number(editing.days || 1)));
      const maxUses = Math.max(1, Math.min(1000, Math.floor(Number(editing.maxUses || 1))));
      await mutate(`/activation-codes/${editing.id}`, 'PATCH', {
        isPermanent: editing.isPermanent,
        durationSeconds: editing.isPermanent ? null : days * 86400,
        maxUses,
        expiresAt: dayEndUnix(editing.expiresAtDay),
        status: editing.status,
        note: editing.note.trim()
      }, crypto.randomUUID());
      setEditing(null);
      setMessage('激活码设置已更新');
      await state.reload();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  async function disable(id: string) {
    const reason = window.prompt('停用原因', '管理员停用');
    if (!reason) return;
    await mutate(`/activation-codes/${id}/disable`, 'POST', { reason }, crypto.randomUUID());
    await state.reload();
  }

  async function remove(id: string) {
    const confirmation = window.prompt('输入“删除激活码”确认永久删除');
    if (confirmation !== '删除激活码') return;
    await mutate(`/activation-codes/${id}`, 'DELETE', { confirmation, reason: '管理员删除激活码' }, crypto.randomUUID());
    await state.reload();
  }

  return <>
    <PageHeader title="激活码" description="生成、复制、调整授权时间、停用和删除客户端激活码" actions={can(identity, 'activation_codes.create') && <button onClick={() => { setError(''); setMessage(''); setForm(defaultForm); }}>生成激活码</button>} />
    <section className="stats-grid compact">
      <StatCard label="总数" value={stats.total} detail="最近 100 条" />
      <StatCard label="可用" value={stats.active} detail="可继续激活" />
      <StatCard label="已耗尽" value={stats.exhausted} detail="达到使用上限" />
      <StatCard label="已停用" value={stats.disabled} detail="不可继续使用" />
    </section>
    {message && <div className="notice success">{message}</div>}
    {error && <div className="notice error">{error}</div>}
    {created.length > 0 && <div className="panel secret-panel">
      <div className="panel-title"><h2>生成结果</h2><button onClick={() => void copyCode(created.join('\n'))}>复制全部</button></div>
      <p>这些激活码已加密保存到记录，之后仍可在列表里复制。</p>
      <pre>{created.join('\n')}</pre>
      <button className="secondary" onClick={() => setCreated([])}>收起</button>
    </div>}
    {state.loading ? <Loading /> : state.error ? <ErrorPanel message={state.error} onRetry={state.reload} /> : <div className="panel table-wrap">
      <table><thead><tr><th>前缀</th><th>完整激活码</th><th>状态</th><th>授权</th><th>使用次数</th><th>激活码到期</th><th>备注/创建者</th><th className="actions-col">操作</th></tr></thead>
        <tbody>{items.map((row) => <tr key={row.id}>
          <td className="mono">{row.codePrefix}…</td>
          <td>
            {row.code ? <div className="code-copy"><span className="mono wrap">{row.code}</span><button className="secondary" onClick={() => void copyCode(row.code || '')}>复制</button></div> : <span className="muted-text">旧记录不可复制</span>}
          </td>
          <td><StatusBadge status={row.status} /></td>
          <td>{isPermanent(row) ? '永久' : `${secondsToDays(row.durationSeconds)} 天`}</td>
          <td><strong>{row.usedCount}</strong> / {row.maxUses}</td>
          <td>{formatTime(row.expiresAt)}</td>
          <td>{row.note || '-'}<small className="block">{row.createdBy} · {formatTime(row.createdAt)}</small></td>
          <td><div className="row-actions">
            {can(identity, 'activation_codes.create') && <button className="secondary" onClick={() => openEdit(row)}>编辑</button>}
            {row.status === 'active' && can(identity, 'activation_codes.disable') && <ConfirmButton danger message="确认停用这个激活码？" onConfirm={() => disable(row.id)}>停用</ConfirmButton>}
            {can(identity, 'activation_codes.disable') && <ConfirmButton danger message="永久删除会移除使用记录，确认继续？" onConfirm={() => remove(row.id)}>删除</ConfirmButton>}
          </div></td>
        </tr>)}</tbody></table>{!items.length && <Empty />}
    </div>}
    {form && <Modal title="生成激活码" onClose={() => setForm(null)}>
      <div className="form-grid">
        {error && <div className="notice error span-2">{error}</div>}
        <label><span>生成数量</span><input type="number" min={1} max={50} value={form.count} onChange={(event) => setForm({ ...form, count: Number(event.target.value) })} /></label>
        <label><span>授权天数</span><input type="number" min={0} value={form.days} onChange={(event) => setForm({ ...form, days: Number(event.target.value) })} /></label>
        <label><span>最大使用次数</span><input type="number" min={1} max={1000} value={form.maxUses} onChange={(event) => setForm({ ...form, maxUses: Number(event.target.value) })} /></label>
        <label><span>备注</span><input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} /></label>
        <p className="form-hint span-2">授权天数填 0 表示永久授权。</p>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={() => setForm(null)}>取消</button><button onClick={create}>生成</button></div>
    </Modal>}
    {editing && <Modal title="编辑激活码" onClose={() => setEditing(null)}>
      <div className="form-grid">
        {error && <div className="notice error span-2">{error}</div>}
        <label className="checkbox-row span-2"><input type="checkbox" checked={editing.isPermanent} onChange={(event) => setEditing({ ...editing, isPermanent: event.target.checked })} /><span>永久授权</span></label>
        {!editing.isPermanent && <label><span>授权天数</span><input type="number" min={1} value={editing.days} onChange={(event) => setEditing({ ...editing, days: Number(event.target.value) })} /></label>}
        <label><span>最大使用次数</span><input type="number" min={1} max={1000} value={editing.maxUses} onChange={(event) => setEditing({ ...editing, maxUses: Number(event.target.value) })} /></label>
        <label><span>激活码有效期</span><input type="date" value={editing.expiresAtDay} onChange={(event) => setEditing({ ...editing, expiresAtDay: event.target.value })} /></label>
        <label><span>状态</span><select value={editing.status} onChange={(event) => setEditing({ ...editing, status: event.target.value as EditForm['status'] })}><option value="active">有效</option><option value="disabled">已停用</option></select></label>
        <label className="span-2"><span>备注</span><input value={editing.note} onChange={(event) => setEditing({ ...editing, note: event.target.value })} /></label>
        <p className="form-hint span-2">“授权天数”控制客户端激活后获得多久授权；“激活码有效期”控制这个激活码本身最晚能用到哪天，留空表示不过期。</p>
      </div>
      <div className="modal-actions"><button className="secondary" onClick={() => setEditing(null)}>取消</button><button onClick={saveEdit}>保存</button></div>
    </Modal>}
  </>;
}
